import React from "react";
import { Stack, Typography, Box, Button, Chip, Skeleton } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { getComplianceColor } from "../../constants/clinicalDashboard";

/**
 * Tab 1 de Panel Insights: Care Gaps
 * Muestra open/closed counts, compliance bars por measure, non-compliant count.
 */
export default function TabCareGaps({ data, loading, onCreateTask }) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <Stack spacing={1.5}>
        <Skeleton variant="rounded" height={50} />
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} variant="rounded" height={28} />
        ))}
      </Stack>
    );
  }

  if (!data) return null;

  const { totals, measures } = data;
  const openPct = totals.open + totals.closed > 0
    ? Math.round((totals.open / (totals.open + totals.closed)) * 100)
    : 0;

  const topMeasures = (measures || []).slice(0, 8);

  return (
    <Stack spacing={2}>
      {/* Summary ring (CSS only) */}
      <Stack direction="row" alignItems="center" spacing={2}>
        {/* Open/Closed display */}
        <Box sx={{ position: "relative", width: 64, height: 64 }}>
          <svg width="64" height="64" viewBox="0 0 64 64">
            <circle cx="32" cy="32" r="28" fill="none" stroke="#E2E8F0" strokeWidth="6" />
            <circle
              cx="32" cy="32" r="28"
              fill="none"
              stroke="#EA580C"
              strokeWidth="6"
              strokeDasharray={`${openPct * 1.76} ${176 - openPct * 1.76}`}
              strokeDashoffset="44"
              strokeLinecap="round"
            />
          </svg>
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.7rem",
            }}
          >
            {openPct}%
          </Typography>
        </Box>
        <Stack spacing={0.25}>
          <Stack direction="row" spacing={1.5}>
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ color: "#EA580C", lineHeight: 1 }}>
                {totals.open.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>Open</Typography>
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ color: "#16A34A", lineHeight: 1 }}>
                {totals.closed.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>Closed</Typography>
            </Box>
          </Stack>
          {totals.nonCompliantCount > 0 && (
            <Chip
              label={`${totals.nonCompliantCount} Non-Compliant`}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.62rem",
                fontWeight: 600,
                bgcolor: "#FEE2E2",
                color: "#991B1B",
                alignSelf: "flex-start",
              }}
            />
          )}
        </Stack>
      </Stack>

      {/* Compliance bars */}
      <Stack spacing={1}>
        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: "uppercase", fontSize: "0.65rem", letterSpacing: 0.5 }}>
          Top Measures
        </Typography>
        {topMeasures.map((m) => (
          <ComplianceBar key={m.name} measure={m} />
        ))}
      </Stack>

      {/* CTAs */}
      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => navigate("/care-gaps")}
          sx={{ textTransform: "none", fontSize: "0.75rem", borderRadius: 2 }}
        >
          Work Care Gaps
        </Button>
        {onCreateTask && (
          <Button
            variant="text"
            size="small"
            onClick={() =>
              onCreateTask({
                title: `Care Gap Review: ${totals.open} open gaps`,
                category: "care_gap",
                priority: "medium",
              })
            }
            sx={{ textTransform: "none", fontSize: "0.72rem", borderRadius: 2, color: "#2563EB" }}
          >
            + Create Task
          </Button>
        )}
      </Stack>
    </Stack>
  );
}

function ComplianceBar({ measure }) {
  const rate = measure.complianceRate ?? 0;
  const color = getComplianceColor(rate);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.25 }}>
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.68rem",
            maxWidth: 180,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={measure.name}
        >
          {measure.name}
        </Typography>
        <Typography variant="caption" fontWeight={600} sx={{ fontSize: "0.68rem", color }}>
          {rate}%
        </Typography>
      </Stack>
      <Box sx={{ height: 5, bgcolor: "#F1F5F9", borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ height: "100%", width: `${Math.min(rate, 100)}%`, bgcolor: color, borderRadius: 2, transition: "width 300ms ease" }} />
      </Box>
    </Box>
  );
}
